import { ArrowRight } from "lucide-react-native";
import { FC, useMemo } from "react";

import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";

import DistributionChart from "./DistributionChart";
import InfoTip from "./InfoTip";
import { formatDelta, formatKpi, KPIS } from "./model";
import { AudienceId, Configuration, KpiDefinition, KpiId, Kpis } from "./types";
import VerbatimCard from "./VerbatimCard";
import { distribution, shareAcceptingPrice, verbatims } from "./voice";

/* ── Types ─────────────────────────────────────── */

interface KpiPanelProps {
  audience: AudienceId;
  audienceLabel: string;
  configuration: Configuration;
  kpis: Kpis;
  /** National figures for the same configuration, for the "vs. national" line. */
  national: Kpis;
  onOpenEvidence: () => void;
  onSelect: (kpi: KpiId) => void;
  /** Optimum figures when the configuration was edited, for "vs. optimal". */
  optimal: Kpis | null;
  sampleSize: number;
  selected: KpiId;
}

interface KpiTabProps {
  delta: number | null;
  /** What the delta is measured against. */
  deltaLabel: string;
  kpi: KpiDefinition;
  onPress: () => void;
  selected: boolean;
  value: number;
}

/* ── Constants ─────────────────────────────────── */

const PURPLE = "#7c3aed";
const QUOTE_COUNT = 3;

/* ── Component ─────────────────────────────────── */

/** KPI tabs with the distribution and verbatims behind the selected one. */
const KpiPanel: FC<KpiPanelProps> = ({
  audience,
  audienceLabel,
  configuration,
  kpis,
  national,
  onOpenEvidence,
  onSelect,
  optimal,
  sampleSize,
  selected,
}) => {
  const definition = KPIS.find((k) => k.id === selected) ?? KPIS[0];
  const bins = useMemo(
    () => distribution(definition, kpis[selected]),
    [definition, kpis, selected]
  );
  const quotes = useMemo(
    () => verbatims(selected, configuration, audience, kpis, QUOTE_COUNT),
    [audience, configuration, kpis, selected]
  );
  const price = Number(configuration.price);
  const priceIndex = bins.findIndex((b) => b.label === `$${price.toFixed(2)}`);
  const readout =
    definition.format === "currency"
      ? `${Math.round(shareAcceptingPrice(kpis.wtp, price))}% of respondents accept $${price.toFixed(2)} or more`
      : `${Math.round(bins[0].share + bins[1].share)}% favourable · ${Math.round(bins[3].share + bins[4].share)}% unfavourable`;

  return (
    <Card className="p-0 rounded-2xl border border-outline-200 overflow-hidden">
      <HStack className="border-b border-outline-200">
        {KPIS.map((kpi, index) => (
          <HStack className="flex-1" key={kpi.id}>
            {index > 0 && <Box className="w-px bg-outline-200" />}
            <KpiTab
              delta={
                optimal
                  ? kpis[kpi.id] - optimal[kpi.id]
                  : audience === "all"
                    ? null
                    : kpis[kpi.id] - national[kpi.id]
              }
              deltaLabel={optimal ? "vs. optimal" : "vs. national"}
              kpi={kpi}
              onPress={() => onSelect(kpi.id)}
              selected={selected === kpi.id}
              value={kpis[kpi.id]}
            />
          </HStack>
        ))}
      </HStack>

      <VStack className="p-6 gap-8">
        <HStack className="items-start justify-between gap-4">
          <VStack className="gap-0.5">
            <Text className="text-sm font-semibold text-typography-900">
              {definition.label} · {audienceLabel} (
              {sampleSize.toLocaleString("en-US")} respondents)
            </Text>
            <Text className="text-xs text-typography-400">
              {definition.description}
            </Text>
          </VStack>
          <Pressable
            className="flex-row items-center gap-1 shrink-0"
            onPress={onOpenEvidence}
          >
            <Text className="text-xs font-medium" style={{ color: PURPLE }}>
              See all responses
            </Text>
            <ArrowRight color={PURPLE} size={12} />
          </Pressable>
        </HStack>

        <VStack className="gap-4">
          <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
            {definition.format === "currency"
              ? "Maximum acceptable price"
              : "How respondents answered"}
          </Text>
          <Box className="w-full max-w-[640px]">
            <DistributionChart
              bins={bins}
              highlight={priceIndex >= 0 ? priceIndex : undefined}
            />
          </Box>
          <Text className="text-xs font-medium text-typography-700">
            {readout}
          </Text>
        </VStack>

        <VStack className="gap-4">
          <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
            What consumers said
          </Text>
          <HStack className="flex-col md:flex-row gap-3 items-stretch">
            {quotes.map((v, i) => (
              <Box className="flex-1 basis-0" key={`${selected}-${i}`}>
                <VerbatimCard verbatim={v} />
              </Box>
            ))}
          </HStack>
        </VStack>
      </VStack>
    </Card>
  );
};

/* ── Subcomponents ─────────────────────────────── */

const KpiTab: FC<KpiTabProps> = ({
  delta,
  deltaLabel,
  kpi,
  onPress,
  selected,
  value,
}) => (
  <Pressable
    className={`flex-1 basis-0 px-4 pt-4 pb-3 border-b-2 ${
      selected
        ? "border-primary-600"
        : "border-transparent hover:bg-background-50"
    }`}
    onPress={onPress}
    style={selected ? { backgroundColor: "#faf8ff" } : undefined}
  >
    <VStack className="gap-1">
      <HStack className="items-start gap-1 min-h-8">
        <Text
          className={`shrink text-xs font-medium leading-4 ${selected ? "text-primary-700" : "text-typography-500"}`}
          numberOfLines={2}
        >
          {kpi.label}
        </Text>
        <Box className="shrink-0 -mt-0.5">
          <InfoTip text={kpi.description} />
        </Box>
      </HStack>
      <Text className="text-2xl font-bold text-typography-900 tabular-nums">
        {formatKpi(kpi, value)}
      </Text>
      <Text
        className={`text-xs font-medium tabular-nums ${
          delta === null || Math.abs(delta) < 0.5
            ? "text-typography-400"
            : delta > 0
              ? "text-success-700"
              : "text-error-700"
        }`}
      >
        {delta === null
          ? "Optimal"
          : `${formatDelta(kpi, delta)} ${deltaLabel}`}
      </Text>
    </VStack>
  </Pressable>
);

export default KpiPanel;
